/**
 * Note:
 * This file should be synced with the corresponding layout in Contenful CMS.
 */

import { ComponentSelector, Slot } from '@thd-olt-component-react/component-selector';
import {
  params,
  arrayOf,
  string,
  fragment,
  shape,
  bool,
  extend,
  customType,
  number,
  alias
} from '@thd-nucleus/data-sources';
import { Hero } from '@thd-olt-component-react/hero';
import { MedioInline, PiccolaPencil } from '@thd-olt-component-react/internal-marketing-banner';
import { Resource } from '@thd-olt-component-react/resource';
import { ContentGalleryCard } from '@thd-olt-component-react/content-gallery-card';
import { SideNavigation } from '@thd-olt-component-react/side-navigation';
import { Spotlight } from '@thd-olt-component-react/spotlight';
import { VisualNavigation, PromoVisualNavigation } from '@thd-olt-component-react/visual-navigation';
import { ProductShelf } from '@thd-olt-component-react/product-shelf';

Slot.dataModel = {
  component: params({
    id: string().isRequired(),
    componentClass: customType('ComponentClass')
      .enum(['Slot'])
      .isRequired(),
  }).shape({
    Slot: fragment().shape({
      componentName: string(),
      id: string(),
      name: string(),
      slotNumber: number(),
      content: shape({
        ...Hero.dataModel.component,
        ...Spotlight.dataModel.component,
        ...Resource.dataModel.component,
      })
    })
  })
};

ComponentSelector.dataModel = {
  component: params({
    id: string().isRequired(),
    componentClass: customType('ComponentClass').enum(['ComponentSelector']).isRequired(),
  }).shape({
    ComponentSelector: fragment().shape({
      componentName: string(),
      id: string(),
      targetingType: shape({
        AdobeTarget: fragment().shape({
          id: string(),
          mbox: string()
        }),
        MultiArmBandit: fragment().shape({
          id: string(),
          campaignId: string()
        })
      }),
      defaultComponent: arrayOf(
        shape({
          ...Hero.dataModel.component,
          ...Slot.dataModel.component
        })
      ),
      variations: arrayOf(
        shape({
          ...Hero.dataModel.component,
          ...Slot.dataModel.component
        })
      ),
    }),
  }),
};

const dynamicSectionsFragment = {
  __typename: string(),
  id: string(),
  title: string(),
  isCarousel: bool(),
  components: arrayOf(shape({
    ...VisualNavigation.dataModel.component,
    ...PromoVisualNavigation.dataModel.component,
    ...Spotlight.dataModel.component,
    ...Resource.dataModel.component,
    ...ComponentSelector.dataModel.component,
    ...ContentGalleryCard.dataModel.component,
  })),
};

const spotlightSectionFragment = {
  id: string(),
  title: string(),
  isCarousel: bool(),
  components: arrayOf(shape({ ...Spotlight.dataModel.component, ...ComponentSelector.dataModel.component })),
};

const resourcesSectionFragment = {
  id: string(),
  title: string(),
  isCarousel: bool(),
  components: arrayOf(
    shape({ ...ComponentSelector.dataModel.component, ...Resource.dataModel.component })
  ),
  __typename: string(),
};

const baseLayoutFragment = {
  id: string(),
  breadcrumb: string(),
  breadcrumbs: shape({
    breadcrumbItem: arrayOf(shape({
      label: string(),
      url: string()
    }))
  }),
  sideNavigation: shape(SideNavigation.dataModel.component),
  // you need this here too because the query is shaped at a lower level
  // it doesnt magically know how to merge the two heroCarousels because the nesting is odd
  // TODO(HeroCarousel): see other HeroCarousel TODO comments. the whole thing can be cleaned up which will allow these comments to be deleted.
  heroCarousel: shape({
    id: string(),
    isCarousel: bool(),
    isAutoplay: bool(),
    components: arrayOf(
      shape({ ...Hero.dataModel.component, ...ComponentSelector.dataModel.component })
    ),
  }),
  sponsoredBottomCarousel: shape({
    __typename: string(),
    customRenderId: string()
  }),
  sponsoredMiddleBanner: shape({
    __typename: string(),
    customRenderId: string()
  }),
  sponsoredSkyscraperBanner: shape({
    __typename: string(),
    customRenderId: string()
  }),
  sponsoredTopBanner: shape({
    __typename: string(),
    customRenderId: string()
  }),
  specialBuyOfTheDay: shape({
    __typename: string(),
    id: string()
  }),
  endCap: shape({
    id: string(),
    __typename: string()
  }),
  descriptionNew: shape({
    title: string(),
    descriptionText: string()
  }),
  relatedSearchesAndProducts: shape({
    id: string()
  }),
  seoLinks: shape({
    id: string()
  }),
  spotlightSection: shape({ ...spotlightSectionFragment }),
  resourcesSection: shape({ ...resourcesSectionFragment }),
  dynamicSections: arrayOf(shape({ ...dynamicSectionsFragment }))
};

const gallerySectionFragment = {
  id: string(),
  title: string(),
  isCarousel: bool(),
  components: arrayOf(
    shape({ ...ComponentSelector.dataModel.component, ...ContentGalleryCard.dataModel.component })
  ),
  __typename: string(),
};

const visNavSectionFragment = {
  id: string(),
  title: string(),
  isCarousel: bool(),
  components: arrayOf(shape({ ...VisualNavigation.dataModel.component })),
};

const promoVisNavSectionFragment = {
  id: string(),
  title: string(),
  isCarousel: bool(),
  components: arrayOf(shape({ ...PromoVisualNavigation.dataModel.component })),
};

const educationalLayoutFragment = fragment().shape({
  ...baseLayoutFragment,
  medioInline1: shape({ ...MedioInline.dataModel.component }),
  medioInline2: shape({ ...MedioInline.dataModel.component }),
  piccolaPencil1: shape({ ...PiccolaPencil.dataModel.component }),
  piccolaPencil2: shape({ ...PiccolaPencil.dataModel.component }),
  productShelf: shape({ ...ProductShelf.dataModel.component }),
  visualNavigation1: shape({ ...visNavSectionFragment }),
  visualNavigation2: shape({ ...visNavSectionFragment }),
  visualNavigation3: shape({ ...visNavSectionFragment }),
  promoVisualNavigation1: shape({ ...promoVisNavSectionFragment }),
  promoVisualNavigation2: shape({ ...promoVisNavSectionFragment }),
  promoVisualNavigation3: shape({ ...promoVisNavSectionFragment }),
});

const navigationalLayoutFragment = fragment().shape({
  ...baseLayoutFragment,
  medioInline1: shape({ ...MedioInline.dataModel.component }),
  medioInline2: shape({ ...MedioInline.dataModel.component }),
  medioInline3: shape({ ...MedioInline.dataModel.component }),
  piccolaPencil1: shape({ ...PiccolaPencil.dataModel.component }),
  piccolaPencil2: shape({ ...PiccolaPencil.dataModel.component }),
  piccolaPencil3: shape({ ...PiccolaPencil.dataModel.component }),
  productShelf: shape({ ...ProductShelf.dataModel.component }),
  visualNavigation1: shape({ ...visNavSectionFragment }),
  visualNavigation2: shape({ ...visNavSectionFragment }),
  visualNavigation3: shape({ ...visNavSectionFragment }),
  visualNavigation4: shape({ ...visNavSectionFragment }),
  visualNavigation5: shape({ ...visNavSectionFragment }),
  visualNavigation6: shape({ ...visNavSectionFragment }),
  promoVisualNavigation1: shape({ ...promoVisNavSectionFragment }),
  promoVisualNavigation2: shape({ ...promoVisNavSectionFragment }),
  promoVisualNavigation3: shape({ ...promoVisNavSectionFragment }),
});

const inspirationalLayoutFragment = fragment().shape({
  ...baseLayoutFragment,
  gallery: shape({ ...gallerySectionFragment }),
  medioInline1: shape({ ...MedioInline.dataModel.component }),
  medioInline2: shape({ ...MedioInline.dataModel.component }),
  piccolaPencil1: shape({ ...PiccolaPencil.dataModel.component }),
  piccolaPencil2: shape({ ...PiccolaPencil.dataModel.component }),
  productShelf: shape({ ...ProductShelf.dataModel.component }),
  visualNavigation1: shape({ ...visNavSectionFragment }),
  visualNavigation2: shape({ ...visNavSectionFragment }),
  promoVisualNavigation1: shape({ ...promoVisNavSectionFragment }),
  promoVisualNavigation2: shape({ ...promoVisNavSectionFragment }),
  promoVisualNavigation3: shape({ ...promoVisNavSectionFragment }),
});

export const educationalLayoutDataModel = extend(
  {
    educationalLayout: alias('layouts').params({
      slug: string().isRequired(),
      customerType: string()
    }).shape({
      slug: string(),
      title: string(),
      type: string(),
      seo: shape({
        canonicalURL: string(),
        description: string(),
        facebookAdmins: string(),
        siteName: string(),
        title: string(),
        type: string(),
        linkData: string(),
        scriptData: string(),
        no_index: string(),
        no_follow: string()
      }),
      content: shape({
        __typename: string(),
        EducationalLayout: educationalLayoutFragment,
      }),
    }),
  }
);

export const navigationalLayoutDataModel = extend(
  {
    navigationalLayout: alias('layouts').params({
      slug: string().isRequired(),
      customerType: string()
    }).shape({
      slug: string(),
      title: string(),
      type: string(),
      seo: shape({
        canonicalURL: string(),
        description: string(),
        facebookAdmins: string(),
        siteName: string(),
        title: string(),
        type: string(),
        linkData: string(),
        scriptData: string(),
        no_index: string(),
        no_follow: string()
      }),
      content: shape({
        __typename: string(),
        NavigationalLayout: navigationalLayoutFragment,
      }),
    }),
  },
);

export const inspirationalLayoutDataModel = extend(
  {
    inspirationalLayout: alias('layouts').params({
      slug: string().isRequired(),
      customerType: string()
    }).shape({
      slug: string(),
      title: string(),
      type: string(),
      seo: shape({
        canonicalURL: string(),
        description: string(),
        facebookAdmins: string(),
        siteName: string(),
        title: string(),
        type: string(),
        linkData: string(),
        scriptData: string(),
        no_index: string(),
        no_follow: string()
      }),
      content: shape({
        __typename: string(),
        InspirationalLayout: inspirationalLayoutFragment,
      }),
    }),
  },
);
